import {
  collection, doc, getDoc, getDocs, query, where, limit, startAt, endAt, orderBy,
} from "firebase/firestore";
import { getDownloadURL, ref } from "firebase/storage";
import { db, storage } from "@/lib/firebase";

type ScryfallCard = any;

type ScryfallRuling = {
  published_at?: string;
  source?: string;
  comment?: string;
  oracle_id?: string;
  card_id?: string;
  [k: string]: any;
};

const CARDS = collection(db, "scryfall_cards");
const RULINGS = collection(db, "scryfall_rulings"); // optional (fallbacks to Storage)

// HTTP-only Scry API client used by QuickSearchOverlay, V2Modal, etc.
// Additive: used for public, backend-powered search and fallbacks.
const BASE =
  (import.meta as any)?.env?.VITE_SCRY_API_BASE ||
  (import.meta as any)?.env?.VITE_API_BASE ||
  "/api"; // Vite proxy in dev or same-origin in prod

function join(base: string, path: string) {
  return `${String(base).replace(/\/+$/, "")}/${String(path).replace(/^\/+/, "")}`;
}

async function httpGet(
  path: string,
  params?: Record<string, string | number | boolean | undefined>,
  signal?: AbortSignal
) {
  const url = new URL(join(BASE, path), window.location.origin);
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
    }
  }
  if ((import.meta as any)?.env?.DEV) {
    // eslint-disable-next-line no-console
    console.debug("[scryApi] GET", url.toString());
  }
  const res = await fetch(url.toString(), { signal, headers: { Accept: "application/json" } });
  if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
  return res.json();
}

// Ensure params type supports `unique`
export type CardsSearchParams = {
  q: string;
  limit?: number;
  page?: number;
  unique?: "prints" | "cards" | "art" | string;
};
// Replace this whole block (type + two duplicate functions) with just the type:
export type CardsSearchResponse = { data: ScryfallCard[]; total?: number; has_more?: boolean };

// Add once (if missing): explicit cards endpoint
// Avoid duplicating this constant if it's already present in the file.
const CARDS_ENDPOINT: string | undefined =
  (import.meta as any)?.env?.VITE_SCRY_CARDS_URL || undefined;

// Helper: only dedupe normal searches (not explicit prints fanout)
function shouldDedupe(q?: string, unique?: string) {
  const uq = (unique || "").toLowerCase();
  if (uq === "prints") return false;
  const t = (q || "").toLowerCase();
  if (t.includes("oracleid:")) return false; // prints fanout keeps all prints
  return true;
}

// Prefer explicit cards endpoint if provided (env), else BASE + /scry/cards/search
// Ensure unique is forwarded
export async function cardsSearch(params: CardsSearchParams, signal?: AbortSignal) {
  // If explicit endpoint is provided, use it
  if (CARDS_ENDPOINT) {
    // Normalize to the search endpoint
   const endpoint = /\/search\/?$/.test(CARDS_ENDPOINT)
     ? CARDS_ENDPOINT
     : `${CARDS_ENDPOINT.replace(/\/+$/, "")}/search`;
   const url = new URL(endpoint, window.location.origin);
    for (const [k, v] of Object.entries(params || {})) {
      if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
    }
    if ((import.meta as any)?.env?.DEV) {
      // eslint-disable-next-line no-console
      console.debug("[scryApi] GET", url.toString());
    }
    try {
      const res = await fetch(url.toString(), { signal, headers: { Accept: "application/json" } });
      if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
      const payload = await res.json();

    // Do NOT collapse when caller asks for unique=prints
   const uniqueNorm = String((params as any)?.unique ?? "").toLowerCase?.() || "";
   if (uniqueNorm === "prints") return payload;

    try {
      if (shouldDedupe(params?.q, params?.unique)) {
        const data = Array.isArray(payload?.data) ? payload.data : [];
        const pruned = collapseUniqueCards(sortByRelease(data));
        return { ...payload, data: pruned };
      }
    } catch {}
    return payload;
  } catch (e) {
    // Ignore fetch errors; fallback to BASE + /scry/cards/search
  }
  }
  // Fallback to BASE + /scry/cards/search
  const payload = await httpGet("scry/cards/search", params, signal);
 // Also respect unique=prints on fallback path
 const uniqueNorm2 = String((params as any)?.unique ?? "").toLowerCase?.() || "";
 if (uniqueNorm2 === "prints") return payload;
  try {
    if (shouldDedupe(params?.q, params?.unique)) {
      const data = Array.isArray((payload as any)?.data) ? (payload as any).data : [];
      const pruned = collapseUniqueCards(sortByRelease(data));
      return { ...(payload as any), data: pruned };
    }
  } catch {}
  return payload;
}




// --- Details enrichment (fallback to Scryfall if Firestore doc is sparse) ---
const DETAIL_FIELDS = ["oracle_text", "type_line", "artist", "set_name", "legalities"] as const;
function isSparse(c: any) {
  if (!c) return true;
  const hasFaceText = Array.isArray(c.card_faces) && c.card_faces.some((f: any) => !!f?.oracle_text);
  if (hasFaceText) return false;
  return !c.oracle_text && !c.type_line && !c.legalities;
}
function mergeCard<T extends object, U extends object>(a: T, b: U): T & U {
  return Object.assign({}, a, b);
}

async function fetchScryfallCardById(id: string, signal?: AbortSignal): Promise<ScryfallCard | null> {
  try {
    const r = await fetch(`https://api.scryfall.com/cards/${encodeURIComponent(id)}`, {
      headers: { Accept: "application/json" }, signal
    });
    if (!r.ok) return null;
    const j = await r.json();
    return j?.object === "card" ? j : null;
  } catch { return null; }
}

// ---- Cards ----
export async function getCardById(id: string, signal?: AbortSignal) {
  let fsCard: any = null;
  try {
    const snap = await getDoc(doc(collection(db, "scryfall_cards"), id));
    fsCard = snap.exists() ? { ...snap.data(), id, object: "card" } : null;
    if (fsCard && !isSparse(fsCard)) return fsCard;
  } catch (e) {
    // Ignore permission errors; continue to enrich from network
  }

  // fallback to Scryfall only to fill missing details
  const sf = await fetchScryfallCardById(id, signal);
  if (!sf) return fsCard;
  const overlay: any = {};
  for (const k of ["oracle_text", "type_line", "artist", "legalities", "set_name", "color_identity", "card_faces"]) {
    if (sf[k] != null) overlay[k] = sf[k];
  }
  return fsCard ? { ...fsCard, ...overlay } : sf;
}

// ---- Prints ----
export async function printsByOracle(oracle_id: string, signal?: AbortSignal) {
  try {
    const qs = await getDocs(query(CARDS, where("oracle_id", "==", oracle_id)));
    return list(sortByRelease(qs.docs.map(toCardFromSnap)));
  } catch (e) {
    // Fallback to backend search when Firestore is unavailable
    try {
      const payload = await cardsSearch({ q: `oracleid:${oracle_id}`, limit: 200, unique: "prints" }, signal);
      const data = Array.isArray((payload as any)?.data) ? (payload as any).data : [];
      return { object: "list", data: sortByRelease(data) };
    } catch {
      return list([]);
    }
  }
}

export async function printsById(id: string, signal?: AbortSignal) {
  try {
    const base = await getCardById(id, signal);
    if (!base) return list([]);
    if (!base.oracle_id) return list([base]);
    return printsByOracle(base.oracle_id, signal);
  } catch {
    // Direct backend prints endpoint for resiliency
    try {
      const payload = await httpGet(`scry/cards/${encodeURIComponent(id)}/prints`, undefined, signal);
      const data = Array.isArray((payload as any)?.data) ? (payload as any).data : [];
      return { object: "list", data: sortByRelease(data) };
    } catch {
      return list([]);
    }
  }
}

export async function getCardByNamedExact(name: string) {
  const lc = name.toLowerCase();
  let qs = await getDocs(query(CARDS, where("name_lower", "==", lc), limit(1)));
  if (qs.empty) qs = await getDocs(query(CARDS, where("name", "==", name), limit(1)));
  if (qs.empty) return null;
  const c = toCardFromSnap(qs.docs[0]);
  // ensure details for named lookups too
  return isSparse(c) ? (await getCardById(c.id)) || c : c;
}

// Backend-friendly named lookup used by ImportDeckModal and others
export async function named(exact: string, signal?: AbortSignal): Promise<any | null> {
  // 1) Try explicit backend named endpoint if available
  try {
    const payload = await httpGet("scry/cards/named", { exact }, signal);
    const card = (payload as any);
    if (card?.id && card?.name) return card;
  } catch {}
  // 2) Fallback to search by exact name via cardsSearch
  try {
    const nm = String(exact || "").replace(/"/g, '\\"');
    const res = await cardsSearch({ q: `name:"${nm}"`, limit: 1 }, signal);
    const data = Array.isArray((res as any)?.data) ? (res as any).data : [];
    return data[0] ?? null;
  } catch {}
  // 3) Last resort: enrich from Firestore named index
  try {
    return await getCardByNamedExact(exact);
  } catch {}
  return null;
}

// ---- Search ----
// Replace Firestore-based search with backend search to avoid permissions issues.
async function searchImpl(_qStr: string, _unique: "cards" | "prints") {
  // Retained for back-compat; no-op path not used when search() below calls cardsSearch()
  return list([]);
}

// Back-compat alias now uses the public backend
export async function search(qStr: string, signal?: AbortSignal) {
  // delegate to cardsSearch so dedupe logic applies
  return cardsSearch({ q: qStr, limit: 24 }, signal);
}

export async function searchPrints(qStr: string, _signal?: AbortSignal) {
  // Keep prints search via Firestore (with HTTP fallback inside printsByOracle)
  const parsed: any = parseQ(qStr);
  if (parsed.oracle_id) {
    return printsByOracle(parsed.oracle_id);
  }
  if (parsed.nameExact) {
    // approximate prints by name (unique cards then expand via oracle later if needed)
    const qs = await getDocs(query(CARDS, where("name_lower", "==", String(parsed.nameExact).toLowerCase())));
    return list(sortByRelease(qs.docs.map(toCardFromSnap)));
  }
  if (parsed.namePrefix) {
    const prefix = String(parsed.namePrefix).toLowerCase().trim();
    if (!prefix) return list([]);
    const qs = await getDocs(query(
      CARDS,
      orderBy("name_lower"),
      startAt(prefix),
      endAt(prefix + "\uf8ff"),
      limit(1000)
    ));
    return list(sortByRelease(qs.docs.map(toCardFromSnap)));
  }
  return list([]);
}

// ---- Rulings (unchanged except for your bucket/env fixes) ----
const RULINGS_PATH = "scryfall/rulings.json";
const RULINGS_URL_ENV = import.meta.env.VITE_SCRYFALL_RULINGS_URL as string | undefined;
let rulingsIndexByOracle: Map<string, ScryfallRuling[]> | null = null;

async function loadRulingsFromStorage(): Promise<Map<string, ScryfallRuling[]>> {
  if (rulingsIndexByOracle) return rulingsIndexByOracle;

  let url: string | null = null;
  if (RULINGS_URL_ENV) url = RULINGS_URL_ENV;
  if (!url) {
    try {
      url = await getDownloadURL(ref(storage, RULINGS_PATH));
    } catch (e) {
      console.warn("[scryApiClient] getDownloadURL failed; set VITE_SCRYFALL_RULINGS_URL or relax Storage rules", e);
      rulingsIndexByOracle = new Map();
      return rulingsIndexByOracle;
    }
  }

  const resp = await fetch(url);
  if (!resp.ok) {
    console.warn("[scryApiClient] rulings.json fetch failed", resp.status);
    rulingsIndexByOracle = new Map();
    return rulingsIndexByOracle;
  }

  const all = (await resp.json()) as ScryfallRuling[];
  const idx = new Map<string, ScryfallRuling[]>();
  for (const r of all) {
    const key = r.oracle_id || r.card_id;
    if (!key) continue;
    const arr = idx.get(key) || [];
    arr.push(r);
    idx.set(key, arr);
  }
  rulingsIndexByOracle = idx;
  return idx;
}

export async function rulingsById(id: string, signal?: AbortSignal) {
  try {
    const sub = await getDocs(collection(doc(CARDS, id), "rulings"));
    if (!sub.empty) return { object: "list", data: sub.docs.map(d => ({ id: d.id, ...d.data() })) as ScryfallRuling[] };
  } catch {}
  try {
    const byCard = await getDocs(query(RULINGS, where("card_id", "==", id)));
    if (!byCard.empty) return { object: "list", data: byCard.docs.map(d => ({ id: d.id, ...d.data() })) as ScryfallRuling[] };
  } catch {}

  const base = await getCardById(id, signal);
  if (base?.oracle_id) {
    try {
      const byOracle = await getDocs(query(RULINGS, where("oracle_id", "==", base.oracle_id)));
      if (!byOracle.empty) return { object: "list", data: byOracle.docs.map(d => ({ id: d.id, ...d.data() })) as ScryfallRuling[] };
    } catch {}

    try {
      const idx = await loadRulingsFromStorage();
      const data = (idx.get(base.oracle_id) || []).slice().sort((a, b) => {
        const da = a.published_at ? Date.parse(a.published_at) : 0;
        const db = b.published_at ? Date.parse(b.published_at) : 0;
        return da - db;
      });
      if (data.length) return { object: "list", data };
    } catch {}

    // last resort: live Scryfall
    try {
      const r = await fetch(`https://api.scryfall.com/cards/oracle/${base.oracle_id}/rulings`, {
        signal, headers: { Accept: "application/json" }
      });
      if (r.ok) {
        const j = await r.json();
        return { object: "list", data: Array.isArray(j.data) ? j.data : [] };
      }
    } catch {}
  }

  return { object: "list", data: [] as ScryfallRuling[] };
}

// ---- Bundle helper ----
export async function resolveBundle(
  keys: { scryfall_id?: string | null; oracle_id?: string | null; name?: string | null },
  signal?: AbortSignal
) {
  let card: ScryfallCard | null = null;

  if (keys.scryfall_id) {
    card = await getCardById(keys.scryfall_id, signal);
  }

  if (!card && keys.oracle_id) {
    const lst = await searchPrints(`oracleid:${keys.oracle_id}`);
    card = lst.data?.[0] || null;
    if (card) {
      // ensure enriched details
      card = await getCardById(card.id, signal);
    }
  }

  if (!card && keys.name) {
    const named = await getCardByNamedExact(keys.name);
    if (named) {
      // ensure enriched details
      card = await getCardById(named.id, signal);
    }
  }

  // Prints for the chosen card (if any)
  let printsList = { object: "list", data: [] as ScryfallCard[] };
  if (card?.id) {
    try {
      printsList = await printsById(card.id, signal);
    } catch {
      printsList = { object: "list", data: [] as ScryfallCard[] };
    }
  }

  // Rulings
  const rulingsList =
    card?.id ? await rulingsById(card.id, signal) : { object: "list", data: [] as any[] };

  return { card: card ?? null, prints: printsList.data, rulings: rulingsList.data ?? [] };
}

// Optional default export for legacy imports
const api = {
  cardsSearch,
  search,
  getCardById,
  printsByOracle,
  printsById,
  getCardByNamedExact,
  searchPrints,
  rulingsById,
  resolveBundle,
  named,
};
export default api;
